import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import OrderStatusDisplay from '../orderStatus/orderStatusDisplay'
import { OrderStatus } from '../../proto/generated/order'
import { formatOrderPriceRupees } from './domains/tradingDomain'
import type { BracketLadder as BracketLadderModel, LadderRole } from './bracketLadderModel'

type LegStatus = {
    status?: OrderStatus
    filledQuantity?: number
    quantity?: number
}

type Props = {
    ladder: BracketLadderModel
    /** Live per-leg OMS state after confirmation, keyed by ladder role. */
    legStatuses?: Partial<Record<LadderRole, LegStatus>>
}

const ROLE_COLOR: Record<LadderRole, string> = {
    TARGET: 'success.main',
    ENTRY: 'text.primary',
    LTP: 'text.secondary',
    STOP: 'error.main'
}

export default function BracketLadder({ ladder, legStatuses }: Props) {
    return (
        <Stack spacing={0.5} className="font-feature-num">
            {ladder.rows.map((row) => {
                const leg = legStatuses?.[row.role]
                const partial = leg?.filledQuantity != null && leg.quantity != null && leg.filledQuantity > 0 && leg.filledQuantity < leg.quantity
                return (
                    <Box
                        key={row.role}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(72px, auto) 1fr auto',
                            alignItems: 'baseline',
                            columnGap: 1,
                            ...(row.role === 'LTP' ? { borderTop: 1, borderColor: 'divider', borderTopStyle: 'dashed', pt: 0.5 } : {})
                        }}
                    >
                        <Typography variant="caption" color={ROLE_COLOR[row.role]} fontWeight={row.role === 'LTP' ? 400 : 600}>
                            {row.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={row.role === 'LTP' ? 400 : 600} textAlign="right">
                            {row.priceLabel}
                            {row.distanceLabel ? (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                    {row.distanceLabel}
                                </Typography>
                            ) : null}
                        </Typography>
                        <Box sx={{ minWidth: 0, textAlign: 'right' }}>
                            {leg?.status != null ? (
                                <Typography variant="caption" component="span">
                                    {partial ? `${leg.filledQuantity}/${leg.quantity} · ` : ''}
                                    <OrderStatusDisplay status={leg.status} />
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                )
            })}
            {ladder.riskRupees != null ? (
                <Typography variant="caption" color="text.secondary" sx={{ pt: 0.25 }}>
                    Risk {formatOrderPriceRupees(ladder.riskRupees)}
                    {ladder.rewardRupees != null ? ` · Reward ${formatOrderPriceRupees(ladder.rewardRupees)}` : ''}
                    {ladder.ratioLabel ? ` · R:R ${ladder.ratioLabel}` : ''}
                </Typography>
            ) : null}
        </Stack>
    )
}
